import {
  BRUSH_PRESETS,
  DEFAULT_STROKE_OPTIONS,
  strokeOutline,
  type DrawDraft,
  type DrawPoint,
  type Presence,
  type UserId,
  type Viewport,
} from '@kanvas/board-core';
import { smoothTowards } from './motion';

/**
 * Strokes other people are drawing right now.
 *
 * Painted from presence rather than from the document: a stroke in progress is
 * not a committed object yet, so it must not enter anyone's undo history.
 *
 * Presence arrives in packets rather than continuously — a peer publishes their
 * stroke a couple of dozen times a second — so painting each packet as it lands
 * makes the tip of the line jump forward in visible steps. Every peer therefore
 * gets a locally advanced tip that eases towards the last received length,
 * which turns those packets back into a line growing evenly at frame rate. The
 * price is a fraction of a packet's worth of lag behind their real pen, which
 * is invisible next to the stepping it removes.
 */

/** Time constant for the painted tip catching up with the received one. */
const CATCH_UP_MS = 50;

/**
 * How long after a peer's last stroke packet their commit still counts as
 * "we watched them draw this".
 */
const HANDOFF_MS = 1200;

interface PeerDraft {
  points: DrawPoint[];
  /** Cumulative arc length up to each point, in board units. */
  lengths: number[];
  total: number;
  /** Arc length actually painted; eases towards `total`. */
  drawn: number;
  color: string;
  size: number;
  brush: DrawDraft['brush'];
}

export class PeerDraftLayer {
  private drafts = new Map<UserId, PeerDraft>();
  private lastTick = 0;

  /**
   * When each peer was last seen mid-stroke. Kept after the stroke ends, which
   * is the whole point: the renderer asks about it once the finished stroke
   * arrives as a document node.
   */
  private lastDraftAt = new Map<UserId, number>();

  /** Folds the latest presence snapshot into the animated state. */
  sync(presence: readonly Presence[], now: number): void {
    const seen = new Set<UserId>();

    for (const peer of presence) {
      const draft = peer.draft;
      if (!draft || draft.points.length === 0) continue;
      seen.add(peer.userId);
      this.lastDraftAt.set(peer.userId, now);

      const existing = this.drafts.get(peer.userId);
      // Presence is re-read every frame but only changes when a packet lands,
      // so an unchanged stroke is not measured again. Colour, width and brush
      // are fixed for the life of a stroke, so there is nothing else to
      // refresh either.
      if (existing && existing.points === draft.points) continue;

      const lengths = cumulativeLengths(draft.points);
      const total = lengths[lengths.length - 1] ?? 0;

      this.drafts.set(peer.userId, {
        points: draft.points,
        lengths,
        total,
        // A shorter stroke than last time is a new stroke, which starts fully
        // drawn rather than sweeping out from its first sample.
        drawn: existing && existing.total <= total ? Math.min(existing.drawn, total) : total,
        color: draft.color,
        size: draft.size,
        brush: draft.brush,
      });
    }

    // Gone from presence means the stroke was committed, abandoned, or its
    // author left: either way the document owns that ink now, if anyone does.
    for (const userId of this.drafts.keys()) {
      if (!seen.has(userId)) this.drafts.delete(userId);
    }
  }

  /** Advances the tips. Returns true while any stroke is still catching up. */
  tick(now: number): boolean {
    const deltaMs = this.lastTick === 0 ? 16 : Math.min(now - this.lastTick, 64);
    this.lastTick = now;

    let growing = false;
    for (const draft of this.drafts.values()) {
      const next = smoothTowards(draft.drawn, draft.total, deltaMs, CATCH_UP_MS);
      if (next !== draft.drawn) growing = true;
      draft.drawn = next;
    }

    for (const [userId, at] of this.lastDraftAt) {
      if (now - at > HANDOFF_MS) this.lastDraftAt.delete(userId);
    }

    return growing;
  }

  /**
   * Whether we just watched this peer draw.
   *
   * The renderer uses this to skip the draw-on replay for the stroke they are
   * about to commit: that ink has been on screen the whole time, and replaying
   * it reads as the board losing their stroke and drawing it over again.
   */
  wasDrawing(userId: UserId, now: number): boolean {
    const at = this.lastDraftAt.get(userId);
    return at !== undefined && now - at < HANDOFF_MS;
  }

  /**
   * The outline is built in screen space because a peer's samples arrive in
   * board units while the width has to track our own zoom.
   */
  paint(ctx: CanvasRenderingContext2D, vp: Viewport): void {
    for (const draft of this.drafts.values()) {
      const points = trimToLength(draft.points, draft.lengths, draft.drawn);
      if (points.length < 2) continue;

      const outline = strokeOutline(
        points.map((p) => [(p[0] - vp.x) * vp.zoom, (p[1] - vp.y) * vp.zoom, p[2]] as DrawPoint),
        {
          ...DEFAULT_STROKE_OPTIONS,
          ...(BRUSH_PRESETS[draft.brush] ?? {}),
          size: draft.size * vp.zoom,
        },
      );
      if (outline.length < 2) continue;

      ctx.save();
      if (draft.brush === 'highlighter') {
        ctx.globalAlpha = 0.4;
        ctx.globalCompositeOperation = 'multiply';
      }
      ctx.fillStyle = draft.color;
      ctx.beginPath();
      ctx.moveTo(outline[0]!.x, outline[0]!.y);
      for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i]!.x, outline[i]!.y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  clear(): void {
    this.drafts.clear();
    this.lastDraftAt.clear();
    this.lastTick = 0;
  }
}

function cumulativeLengths(points: readonly DrawPoint[]): number[] {
  const out = [0];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    out.push(out[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  return out;
}

/**
 * Cuts the stroke at an arc length rather than at a sample index: freehand
 * samples bunch up on slow curves and spread out on fast ones, so an
 * index-based cut would race through the straights and crawl round the bends.
 */
function trimToLength(
  points: readonly DrawPoint[],
  lengths: readonly number[],
  drawn: number,
): DrawPoint[] {
  const total = lengths[lengths.length - 1] ?? 0;
  if (drawn >= total) return points as DrawPoint[];

  const out: DrawPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    if (lengths[i]! <= drawn) {
      out.push(points[i]!);
      continue;
    }
    // Partial segment: place the tip exactly where the reveal has reached.
    const a = points[i - 1];
    if (!a) break;
    const span = lengths[i]! - lengths[i - 1]!;
    const k = span === 0 ? 0 : (drawn - lengths[i - 1]!) / span;
    const b = points[i]!;
    out.push([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]);
    break;
  }
  return out;
}
